import { useNavigate } from "react-router-dom";
import {
  Settings,
  ArrowDown,
  ArrowRight,
  CheckCircle,
  Clock,
  FileText,
  BookOpen,
  Link2,
} from "lucide-react";

interface SiklusProgress {
  kode: string;
  nama: string;
  uploaded_count: number;
  completion_pct: number;
  status_color: string;
}

interface SOPItem {
  id: string;
  judul: string;
  nomor_dokumen?: string;
  status_kode: string;
  status_nama: string;
}

interface ProsesBisnisPanelProps {
  items: SiklusProgress[];
  sopList: SOPItem[];
  onSiklusClick?: (kode: string) => void;
}

const COLOR_MAP: Record<string, { bg: string; bar: string; text: string; dot: string }> = {
  green: { bg: "bg-green-50", bar: "bg-green-500", text: "text-green-700", dot: "bg-green-500" },
  yellow: { bg: "bg-yellow-50", bar: "bg-yellow-500", text: "text-yellow-700", dot: "bg-yellow-500" },
  red: { bg: "bg-red-50", bar: "bg-red-500", text: "text-red-700", dot: "bg-red-500" },
};

const SOP_STATUS_ICON: Record<string, { icon: typeof CheckCircle; color: string }> = {
  DITERBITKAN: { icon: CheckCircle, color: "text-green-500" },
  DISETUJUI: { icon: CheckCircle, color: "text-emerald-500" },
  DITINJAU: { icon: Clock, color: "text-blue-500" },
  DIAJUKAN: { icon: Clock, color: "text-yellow-500" },
  DRAFT: { icon: Clock, color: "text-gray-400" },
};

const ALUR_PROSES = ["Perencanaan", "Penetapan", "Penagihan", "Pembayaran", "Pelaporan", "Audit"];

export default function ProsesBisnisPanel({ items, sopList, onSiklusClick }: ProsesBisnisPanelProps) {
  const navigate = useNavigate();
  const totalDocs = items.reduce((sum, s) => sum + s.uploaded_count, 0);

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border-b border-emerald-100">
        <Settings className="h-4 w-4 text-emerald-600" />
        <h3 className="text-sm font-bold text-emerald-800">PROSES BISNIS</h3>
        <span className="text-xs text-emerald-500 ml-1">(D03, D04 - Siklus & SOP)</span>
      </div>

      <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
        {/* ── Siklus Keuangan ── */}
        <div className="p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-3">
            Siklus Keuangan
          </p>
          <div className="space-y-1">
            {items.map((siklus, index) => {
              const colors = COLOR_MAP[siklus.status_color] || COLOR_MAP.red;
              return (
                <div key={siklus.kode}>
                  <button
                    onClick={() => onSiklusClick?.(siklus.kode)}
                    className={`w-full rounded-lg p-3 ${colors.bg} hover:opacity-90 transition-opacity text-left`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className={`h-2.5 w-2.5 rounded-full ${colors.dot}`} />
                        <span className="font-mono text-xs font-bold text-gray-700">{siklus.kode}</span>
                        <span className="text-sm font-medium text-gray-800">{siklus.nama}</span>
                      </div>
                      <span className={`text-xs font-semibold ${colors.text}`}>
                        {siklus.uploaded_count} dok
                      </span>
                    </div>
                    <div className="h-2 bg-white/60 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${colors.bar} rounded-full transition-all`}
                        style={{ width: `${Math.min(siklus.completion_pct, 100)}%` }}
                      />
                    </div>
                    <p className={`text-xs mt-1 ${colors.text}`}>
                      {siklus.completion_pct.toFixed(0)}% dari total
                    </p>
                  </button>
                  {index < items.length - 1 && (
                    <div className="flex justify-center py-0.5">
                      <ArrowDown className="h-4 w-4 text-gray-300" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {totalDocs > 0 && (
            <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between text-sm">
              <span className="text-gray-500">Total Dokumen</span>
              <span className="font-bold text-gray-900">{totalDocs}</span>
            </div>
          )}
        </div>

        {/* ── SOP & Juknis ── */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="h-3.5 w-3.5 text-emerald-600" />
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
              SOP & Juknis
            </p>
          </div>

          {sopList.length === 0 ? (
            <p className="text-xs text-gray-400 py-2">Tidak ada SOP/Juknis</p>
          ) : (
            <div className="space-y-1.5">
              {sopList.slice(0, 5).map((sop) => {
                const statusConf = SOP_STATUS_ICON[sop.status_kode] || SOP_STATUS_ICON.DRAFT;
                const Icon = statusConf.icon;
                return (
                  <button
                    key={sop.id}
                    onClick={() => navigate(`/documents/${sop.id}`)}
                    className="w-full flex items-center gap-2 rounded-lg px-2.5 py-2 hover:bg-gray-50 transition-colors text-left"
                  >
                    <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${statusConf.color}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate">{sop.judul}</p>
                      {sop.nomor_dokumen && (
                        <p className="text-[10px] text-gray-400 font-mono">{sop.nomor_dokumen}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Alur Proses ── */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="h-3.5 w-3.5 text-emerald-600" />
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
              Alur Proses
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {ALUR_PROSES.map((step, index) => (
              <div key={step} className="flex items-center gap-1">
                <span className="rounded bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
                  {step}
                </span>
                {index < ALUR_PROSES.length - 1 && (
                  <ArrowRight className="h-3 w-3 text-gray-300 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
